// ── WATCHED FUNDERS ──────────────────────────────────────────
// The funders that publish open calls on an ordinary web page — no feed, no
// API. Paste the page in; NofoDesk reads it daily and lists what is open.
// The reading rules live on the server (lib/watch.js, routes/watch.js).
//
// Built on useThemedTokens from the start, so it follows light and dark mode.
import { useState, useEffect, useCallback } from "react";
import { Eye, ExternalLink, RefreshCw, Trash2, TrendingUp, Plus } from "lucide-react";
import { useThemedTokens } from "../theme";
import { API, apiFetch } from "../utils/api";

const GREEN = "#22A06B", AMBER = "#D97706", RED = "#DC2626", ACCENT = "#5B4FE9";

const ago = (iso) => {
  if (!iso) return "not yet";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};
const daysLeft = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`).getTime() - Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
  return Math.round(d / 86400000);
};
const isNew = (iso) => iso && (Date.now() - new Date(iso).getTime()) < 7 * 86400000;

// What a page's last check means, in words.
function pageStatus(p) {
  switch (p.last_status) {
    case "ok":           return { tone: GREEN, text: `${p.calls_found} open call${p.calls_found === 1 ? "" : "s"}` };
    case "unreadable":   return { tone: AMBER, text: "Can't read this page", detail: p.last_error };
    case "list_missing": return { tone: AMBER, text: "Can't see the list", detail: p.last_error };
    case "error":        return { tone: RED,   text: "Check failed", detail: p.last_error };
    default:             return { tone: null,  text: "Waiting for first check" };
  }
}

export default function WatchedFunders({ onExtract, onCount }) {
  const T = useThemedTokens();
  const [pages, setPages]   = useState([]);
  const [calls, setCalls]   = useState([]);
  const [limit, setLimit]   = useState(15);
  const [url, setUrl]       = useState("");
  const [label, setLabel]   = useState("");
  const [busy, setBusy]     = useState("");          // "" | "add" | page id being checked
  const [note, setNote]     = useState(null);        // { tone, text }
  const [confirm, setConfirm] = useState(null);      // page id awaiting a second click to remove
  const [loaded, setLoaded] = useState(false);

  const apply = useCallback((d) => {
    setPages(d.pages || []); setCalls(d.calls || []); if (d.limit) setLimit(d.limit);
    if (onCount) onCount((d.calls || []).length);
  }, [onCount]);

  useEffect(() => {
    apiFetch(`${API}/watch`).then((r) => r.json()).then(apply).catch(() => {}).finally(() => setLoaded(true));
  }, [apply]);

  const add = async () => {
    if (!url.trim()) return;
    setBusy("add"); setNote(null);
    try {
      const res = await apiFetch(`${API}/watch`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim(), label: label.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "That page could not be added.");
      apply(d); setUrl(""); setLabel("");
      const r = d.result || {};
      setNote(r.status === "ok"
        ? { tone: GREEN, text: r.calls ? `Watching. Found ${r.calls} open call${r.calls === 1 ? "" : "s"} on that page.` : "Watching. No open calls on that page right now — NofoDesk will check it daily." }
        : { tone: AMBER, text: "Added, but there was a problem reading it — see the page below." });
    } catch (e) { setNote({ tone: RED, text: e.message }); }
    setBusy("");
  };

  const check = async (id) => {
    setBusy(id); setNote(null);
    try {
      const res = await apiFetch(`${API}/watch/${id}/check`, { method: "POST" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "That check did not complete.");
      apply(d);
      const r = d.result || {};
      if (r.fresh) setNote({ tone: GREEN, text: `${r.fresh} new call${r.fresh === 1 ? "" : "s"} found.` });
    } catch (e) { setNote({ tone: AMBER, text: e.message }); }
    setBusy("");
  };

  // Two clicks rather than a browser confirm box: a dialog would block the
  // page, and a quick second click is just as deliberate.
  const remove = async (id) => {
    if (confirm !== id) { setConfirm(id); return; }
    setConfirm(null);
    try {
      const res = await apiFetch(`${API}/watch/${id}`, { method: "DELETE" });
      const d = await res.json();
      if (res.ok) apply(d); else setNote({ tone: RED, text: d.error || "That page could not be removed." });
    } catch { setNote({ tone: RED, text: "That page could not be removed." }); }
  };

  const card  = { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: "16px 18px" };
  const input = { flex: 1, minWidth: 180, padding: "9px 12px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.textPrimary, fontSize: 13, fontFamily: T.fontUI, outline: "none" };
  const btn   = (primary) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: T.radiusMd, fontSize: 13, fontFamily: T.fontUI, cursor: "pointer", fontWeight: 500, border: `1px solid ${primary ? ACCENT : T.border}`, background: primary ? ACCENT : "transparent", color: primary ? "#fff" : T.textSecondary });
  const label12 = { fontSize: 12, color: T.textMuted, fontFamily: T.fontUI };
  const pageById = Object.fromEntries(pages.map((p) => [p.id, p]));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* ── Add a page ── */}
      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, fontFamily: T.fontUI, marginBottom: 4 }}>Watch a funder's page</div>
        <div style={{ ...label12, marginBottom: 12, lineHeight: 1.55 }}>
          Paste the page where a funder lists its open calls. NofoDesk reads it every day and adds what is open here.
          Links always come from the page itself — nothing is made up.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input style={input} placeholder="https://funder.org/funding-opportunities" value={url}
            onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <input style={{ ...input, flex: "0 1 200px", minWidth: 140 }} placeholder="Name (optional)" value={label}
            onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <button style={{ ...btn(true), opacity: busy === "add" || !url.trim() ? 0.6 : 1 }} disabled={busy === "add" || !url.trim()} onClick={add}>
            {busy === "add" ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Reading the page…</> : <><Plus size={14} /> Watch</>}
          </button>
        </div>
        {note && <div style={{ marginTop: 10, fontSize: 13, fontFamily: T.fontUI, color: note.tone, lineHeight: 1.5 }}>{note.text}</div>}
        <div style={{ ...label12, marginTop: 10 }}>{pages.length} of {limit} pages watched</div>
      </div>

      {/* ── Watched pages ── */}
      {pages.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, fontFamily: T.fontUI, fontWeight: 600, marginBottom: 6 }}>Pages you watch</div>
          {pages.map((p) => {
            const st = pageStatus(p);
            return (
              <div key={p.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderTop: `1px solid ${T.border}`, flexWrap: "wrap" }}>
                <Eye size={15} style={{ color: T.textMuted, marginTop: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.textPrimary, fontFamily: T.fontUI }}>{p.label}</div>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: T.textMuted, fontFamily: T.fontUI, wordBreak: "break-all" }}>{p.url}</a>
                  <div style={{ fontSize: 12, fontFamily: T.fontUI, marginTop: 4 }}>
                    <span style={{ color: st.tone || T.textMuted, fontWeight: 600 }}>{st.text}</span>
                    <span style={{ color: T.textMuted }}> · checked {ago(p.last_checked_at)}</span>
                  </div>
                  {st.detail && <div style={{ fontSize: 12, color: T.textSecondary, fontFamily: T.fontUI, marginTop: 4, lineHeight: 1.5 }}>{st.detail}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={btn(false)} disabled={!!busy} onClick={() => check(p.id)}>
                    <RefreshCw size={13} style={{ animation: busy === p.id ? "spin 1s linear infinite" : "none" }} />
                    {busy === p.id ? "Checking…" : "Check now"}
                  </button>
                  <button style={{ ...btn(false), color: confirm === p.id ? RED : T.textMuted, borderColor: confirm === p.id ? RED : T.border }}
                    onClick={() => remove(p.id)} onBlur={() => setConfirm(null)}>
                    <Trash2 size={13} />{confirm === p.id ? "Click again to remove" : "Remove"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Open calls found ── */}
      {calls.length > 0 && (
        <div style={{ display: "grid", gap: 10 }}>
          {calls.map((c) => {
            const left = daysLeft(c.deadline);
            const from = pageById[c.page_id];
            return (
              <div key={c.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, fontFamily: T.fontUI, lineHeight: 1.4 }}>{c.title}</div>
                  {isNew(c.first_seen_at) && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: GREEN, border: `1px solid ${GREEN}`, borderRadius: 999, padding: "1px 8px", fontFamily: T.fontUI, whiteSpace: "nowrap" }}>New</span>
                  )}
                </div>
                <div style={{ ...label12, marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <span>{from?.label || "Watched page"}</span>
                  {c.deadline
                    ? <span style={{ color: left <= 7 ? RED : left <= 30 ? AMBER : T.textMuted, fontWeight: 600 }}>
                        Closes {new Date(`${c.deadline}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
                        {left != null && ` · ${left === 0 ? "today" : `${left} days left`}`}
                      </span>
                    : <span>No deadline stated</span>}
                  {c.amount_text && <span>{c.amount_text}</span>}
                </div>
                {c.description && <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.fontUI, marginTop: 8, lineHeight: 1.55 }}>{c.description}</div>}
                {!c.link_on_page && (
                  <div style={{ ...label12, marginTop: 6 }}>The page has no separate link for this call, so Open goes to the funder's page.</div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ ...btn(false), textDecoration: "none" }}>
                    <ExternalLink size={13} /> Open
                  </a>
                  <button style={{ ...btn(false), color: ACCENT, borderColor: ACCENT }} onClick={() => onExtract && onExtract({ url: c.url, title: c.title })}>
                    <TrendingUp size={13} /> Extract Full Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loaded && pages.length > 0 && calls.length === 0 && (
        <div style={{ ...card, ...label12, fontSize: 13, lineHeight: 1.55 }}>
          No open calls on your watched pages right now. NofoDesk checks them every morning and will list new calls here as they appear.
        </div>
      )}
    </div>
  );
}
